/** The top of the node inspector: the name (renamed in place) and the tags. */
import { For, Show, createSignal, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { categoryVar } from "../../shared/categories";
import { boxValues } from "../../shared/fields";

/** The node's name (double-click to rename), kind, categories, its environment's short values (a language), id and source file. */
export function NodeHeader(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { actions, graph, kinds, fields } = useApp();
  /** Sends a change of this node to the core. */
  const update = (patch: Parameters<typeof actions.updateNode>[1]) => actions.updateNode(n().id, patch);
  const [renaming, setRenaming] = createSignal(false);
  return (
    <>
      <div class="ihead">
        <Show
          when={renaming()}
          fallback={
            <h2 data-node-title onDblClick={() => setRenaming(true)} title="Double-click to rename">
              {n().name}
            </h2>
          }
        >
          <input
            class="field rename"
            value={n().name}
            aria-label="Name"
            ref={(el) => queueMicrotask(() => (el.focus(), el.select()))}
            onKeyDown={(e) => {
              if (e.key === "Escape") setRenaming(false);
              if (e.key === "Enter") {
                const name = e.currentTarget.value.trim();
                setRenaming(false);
                if (name && name !== n().name) void update({ name });
              }
            }}
            onBlur={() => setRenaming(false)}
          />
        </Show>
        <div class="tags">
          <span class="tag">{kinds.ofNode(n()).label}</span>
          <For each={n().categories}>
            {(c) => (
              <span class="tag" data-node-category={c}>
                <i class="dot" style={{ background: `var(${categoryVar(graph.categories(), c)})` }} />
                {c}
              </span>
            )}
          </For>
          <For each={boxValues(n(), fields.list())}>{(value) => <span class="tag mono">{value}</span>}</For>
          <span class="tag mono muted" title="Id, used by links">
            {n().id}
          </span>
        </div>
        <Show when={n().source}>
          <p class="mono small muted" data-node-source title="Where a project scan found it (relative to the scanned folder)">
            {n().source}
          </p>
        </Show>
      </div>
    </>
  );
}
